/**
 * Admin Debug Utilities
 * Tools for debugging admin status issues
 */

import { verifyAdminStatus, getCacheStats, invalidateAdminCache } from '../services/adminVerification';

// History of admin status checks (last 50)
const statusHistory = [];
const MAX_HISTORY = 50;

/**
 * Log admin status change
 */
export function logAdminStatus(userId, status, source = 'unknown') {
  const entry = {
    userId,
    status,
    source,
    timestamp: new Date().toISOString(),
    timestampMs: Date.now(),
  };

  statusHistory.push(entry);
  
  // Keep only last MAX_HISTORY entries
  if (statusHistory.length > MAX_HISTORY) {
    statusHistory.shift();
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Admin Debug] ${source}: User ${userId} is ${status ? 'ADMIN' : 'NOT ADMIN'}`);
  }
}

/**
 * Check admin status manually (for debugging)
 */
export async function checkAdminStatus(userId) {
  console.log(`[Admin Debug] Checking admin status for user: ${userId}`);
  
  const result = await verifyAdminStatus(userId);
  
  logAdminStatus(userId, result.isAdmin, 'manual_check');
  
  console.log('[Admin Debug] Result:', {
    isAdmin: result.isAdmin,
    role: result.role,
    fromCache: result.fromCache,
    error: result.error,
    verifiedAt: result.verifiedAt ? new Date(result.verifiedAt).toISOString() : null,
  });

  return result;
}

/**
 * Get admin status history
 */
export function getAdminStatusHistory() {
  return [...statusHistory];
}

/**
 * Clear admin status history
 */
export function clearAdminStatusHistory() {
  statusHistory.length = 0;
  console.log('[Admin Debug] Status history cleared');
}

/**
 * Get debug information
 */
export function getDebugInfo() {
  const cacheStats = getCacheStats();
  
  return {
    cacheStats,
    history: getAdminStatusHistory(),
    historyCount: statusHistory.length,
  };
}

/**
 * Print debug information to console
 */
export function printDebugInfo() {
  const info = getDebugInfo();
  
  console.group('[Admin Debug] Debug Information');
  console.log('Cache Stats:', info.cacheStats);
  console.log('Status History:', info.history);
  console.log('History Count:', info.historyCount);
  console.groupEnd();
}

/**
 * Clear all caches (for testing)
 */
export function clearAllCaches() {
  invalidateAdminCache();
  clearAdminStatusHistory();
  console.log('[Admin Debug] All caches cleared');
}

// Make functions available globally in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.adminDebug = {
    checkAdminStatus,
    getAdminStatusHistory,
    clearAdminStatusHistory,
    getDebugInfo,
    printDebugInfo,
    clearAllCaches,
    logAdminStatus,
  };
  
  console.log('[Admin Debug] Debug utilities available at window.adminDebug');
}
